import React, { useState, useEffect } from 'react';
import { useErrors } from '@contexts/ErrorsContext';
import { getErrorMessage } from '@utils/commons';
// eslint-disable-next-line import/no-cycle
import { haveLpaOptions } from '../utils';

export interface NextOfKinDetailsType {
  nextOfKinStatus: string;
  nfkFullName: string;
  nfkPhoneNumber: string;
  nfkRelationship: string;
  haveLPA: string;
}

interface NextOfKinDetailsProps {
  nextOfKinDetails: NextOfKinDetailsType;
  setNextOfKinDetails: React.Dispatch<
    React.SetStateAction<NextOfKinDetailsType>
  >;
}

const NextOfKinDetails: React.FC<NextOfKinDetailsProps> = ({
  nextOfKinDetails,
  setNextOfKinDetails,
}) => {
  const [nfkErrorMessage, setNfkErrorMessage] = useState<{
    nfkFullName: string;
    nfkRelationship: string;
  }>({ nfkFullName: '', nfkRelationship: '' });
  const { errors } = useErrors();

  useEffect(() => {
    setNfkErrorMessage({
      nfkFullName: getErrorMessage(errors, 'nfkFullName') || '',
      nfkRelationship: getErrorMessage(errors, 'nfkRelationship') || '',
    });
  }, [errors]);
  return (
    <div>
      <p className="govuk-body">
        Your Next of Kin cannot consent or withhold consent for you. They can
        however express views about your care. We recommend that you have
        conversations with your Next of Kin about your care wishes during the
        Advance Care Planning process.
      </p>
      <h3 className="govuk-heading-s">Next of Kin details</h3>
      <div id="next-of-kin-hint" className="govuk-hint">
        What are the details for your primary next of kin?
      </div>
      <div
        className={`govuk-form-group ${nfkErrorMessage.nfkFullName ? 'govuk-form-group--error' : ''}`}
      >
        <label className="govuk-label" htmlFor="nfkFullName">
          Full name
        </label>
        {nfkErrorMessage.nfkFullName && (
          <p id="full-name-input-error" className="govuk-error-message">
            <span className="govuk-visually-hidden">Error:</span>
            {nfkErrorMessage.nfkFullName}
          </p>
        )}
        <input
          className={`govuk-input govuk-input--width-20  ${nfkErrorMessage.nfkFullName ? 'govuk-input--error' : ''}`}
          id="nfkFullName"
          name="nfkFullName"
          type="text"
          autoComplete="nfkFullName"
          aria-describedby="next-of-kin-full-name"
          value={nextOfKinDetails.nfkFullName}
          onChange={e =>
            setNextOfKinDetails({
              ...nextOfKinDetails,
              nfkFullName: e.target.value,
            })
          }
        />
      </div>
      <div
        className={`govuk-form-group ${nfkErrorMessage.nfkRelationship ? 'govuk-form-group--error' : ''}`}
      >
        <label className="govuk-label" htmlFor="nfkFullName">
          Relationship
        </label>
        {nfkErrorMessage.nfkRelationship && (
          <p id="full-name-input-error" className="govuk-error-message">
            <span className="govuk-visually-hidden">Error:</span>
            {nfkErrorMessage.nfkRelationship}
          </p>
        )}
        <input
          className={`govuk-input govuk-input--width-20  ${nfkErrorMessage.nfkRelationship ? 'govuk-input--error' : ''}`}
          id="nfkRelationship"
          name="nfkRelationship"
          type="text"
          autoComplete="nfkRelationship"
          aria-describedby="next-of-kin-relationship"
          value={nextOfKinDetails.nfkRelationship}
          onChange={e =>
            setNextOfKinDetails({
              ...nextOfKinDetails,
              nfkRelationship: e.target.value,
            })
          }
        />
      </div>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="phone-number">
          Phone number
        </label>
        <input
          className="govuk-input govuk-input--width-20"
          id="phone-number"
          name="nfkPhoneNumber"
          type="tel"
          autoComplete="tel"
          aria-describedby="next-of-kin-phone-number"
          maxLength={11} // Limits input to 11 characters
          value={nextOfKinDetails.nfkPhoneNumber}
          onChange={e => {
            const { value } = e.target;
            // Check if value only contains numbers and has a maximum of 11 digits
            if (/^[0-9]*$/.test(value) && value.length <= 11) {
              setNextOfKinDetails({
                ...nextOfKinDetails,
                nfkPhoneNumber: value,
              });
            }
          }}
        />
      </div>
      <div className="govuk-form-group">
        <fieldset className="govuk-fieldset">
          <legend className="govuk-fieldset__legend">
            Does the Next of Kin have a Lasting Power of Attorney for the
            resident’s Health and Welfare?
          </legend>
          <div
            className="govuk-radios care-status__radios"
            data-module="govuk-radios"
          >
            {haveLpaOptions.map(option => {
              return (
                <div className="govuk-radios__item" key={option.value}>
                  <input
                    className="govuk-radios__input"
                    id="haveLPA"
                    name="haveLPA"
                    type="radio"
                    value={option.value}
                    checked={nextOfKinDetails.haveLPA === option.value}
                    onChange={e =>
                      setNextOfKinDetails({
                        ...nextOfKinDetails,
                        haveLPA: e.target.value,
                      })
                    }
                  />
                  <label
                    className="govuk-label govuk-radios__label"
                    htmlFor={option.value}
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default NextOfKinDetails;
